import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  REPORTS_BASE_URL,
  REPORTS_DASHBOARD_API,
} from "../../../Constants/Config";
import { useAuthDetails } from "../../../Common/cookiesHelper";

const initialState = {
  loading: true,
  dashboardData: null,
  kpis: {
    revenue: { value: 0, change: 0 },
    profit: { value: 0, change: 0 },
    transactions: { value: 0, change: 0 },
    customers: { value: 0, change: 0 },
    aov: { value: 0, change: 0 },
    items_per_txn: { value: 0, change: 0 },
    discount_amt: { value: 0, change: 0 },
    discount_pct: { value: 0, change: 0 },
  },
  trend: [],
  intraPeriodTrend: [],
  topProducts: [],
  recentActivity: [],
  error: "",
  isCancel: false,
};

let controller = null;

/**
 * Consolidated dashboard API call
 * Replaces individual calls to:
 * - revenue_data_api
 * - sales_count_api
 * - customer_count_api
 * - gross_profit_api
 * - avg_sales_value
 * - avg_item_sale
 * - discount_api_new
 * - discount_in_per_api_new
 * - top_sold_products
 * - get-recent-activity
 */
export const getDashboardData = createAsyncThunk(
  "consolidatedDashboard/getDashboardData",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();

    if (controller) {
      controller.abort();
    }

    controller = new AbortController();

    try {
      const { token, ...otherUserData } = userTypeData;

      // The caller (NewDashboardMain) already builds a complete payload with
      // merchant_id, outlet_ids, start_date, end_date, date_range and activeType.
      // We pass it through as-is so the trend windows line up with the UI.
      const payload = { ...data };

      const apiUrl = REPORTS_BASE_URL + REPORTS_DASHBOARD_API;

      const response = await axios.post(
        apiUrl,
        { ...payload, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError") {
        return rejectWithValue({ isCanceled: true });
      }
      const customError = {
        message: error.message,
        status: error.response ? error.response.status : "Network Error",
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const consolidatedDashboardSlice = createSlice({
  name: "consolidatedDashboard",
  initialState,
  reducers: {
    clearDashboardData: (state) => {
      state.loading = true;
      state.dashboardData = null;
      state.kpis = {
        revenue: { value: 0, change: 0 },
        profit: { value: 0, change: 0 },
        transactions: { value: 0, change: 0 },
        customers: { value: 0, change: 0 },
        aov: { value: 0, change: 0 },
        items_per_txn: { value: 0, change: 0 },
        discount_amt: { value: 0, change: 0 },
        discount_pct: { value: 0, change: 0 },
      };
      state.trend = [];
      state.intraPeriodTrend = [];
      state.topProducts = [];
      state.recentActivity = [];
      state.error = "";
      state.isCancel = false;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getDashboardData.pending, (state) => {
      state.loading = true;
      state.isCancel = false;
    });
    builder.addCase(getDashboardData.fulfilled, (state, action) => {
      state.loading = false;
      state.dashboardData = action.payload;
      
      if (action.payload?.kpis) {
        state.kpis = action.payload.kpis;
      }
      
      state.trend = action.payload?.trend || [];
      state.intraPeriodTrend = action.payload?.intra_period_trend || [];
      state.topProducts = action.payload?.top_products || [];
      state.recentActivity = action.payload?.recent_activity || [];
      state.error = "";
      state.isCancel = false;
    });
    builder.addCase(getDashboardData.rejected, (state, action) => {
      state.loading = false;
      state.dashboardData = null;
      state.kpis = {
        revenue: { value: 0, change: 0 },
        profit: { value: 0, change: 0 },
        transactions: { value: 0, change: 0 },
        customers: { value: 0, change: 0 },
        aov: { value: 0, change: 0 },
        items_per_txn: { value: 0, change: 0 },
        discount_amt: { value: 0, change: 0 },
        discount_pct: { value: 0, change: 0 },
      };
      state.trend = [];
      state.intraPeriodTrend = [];
      state.topProducts = [];
      state.recentActivity = [];
      state.error = action.error.message;
      state.isCancel = action.payload?.isCanceled || false;
    });
  },
});

export const { clearDashboardData } = consolidatedDashboardSlice.actions;
export default consolidatedDashboardSlice.reducer;
